"use client";

import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { PublicConfig } from "apps/website/config/public";
import { useAuth } from "@auth/client-sdk-react";
import {
  checkNibblesPouchesUpsellEligibility,
} from "apps/website/utils/website/nibbles-pouches-upsell-eligibility";
import {
  NIBBLES_POUCHES_UPSELL_OVERRIDE_KEY,
  NIBBLES_POUCHES_UPSELL_QUERY_KEY,
  NIBBLES_POUCHES_UPSELL_SUBSCRIPTION_ID_KEY,
} from "apps/website/utils/website/nibbles-pouches-upsell-query-params";
import {
  FreshExtraProduct,
  SubscriptionsAPI,
} from "@api-clients/subscriptions";

import NibblesPouchesUpsellModal, {
  NibblesPouchesPurchaseState,
} from "../feature/Modal/NibblesPouchesUpsellModal/NibblesPouchesUpsellModal";

type NibblesPouchesEligibilityState = "eligible" | "none";

// Query param constants re-exported for convenience - defined in
// nibbles-pouches-upsell-query-params.ts (a plain module, not "use client")
// so the server-rendered routing page can safely import them too. Exports
// from a "use client" file become opaque client references when imported
// into a Server Component, even for plain string constants.
export {
  NIBBLES_POUCHES_UPSELL_OVERRIDE_KEY,
  NIBBLES_POUCHES_UPSELL_QUERY_KEY,
  NIBBLES_POUCHES_UPSELL_SUBSCRIPTION_ID_KEY,
};

const MANAGE_EXTRAS_ROUTE = "/customer/my-plans/manage-extras";

const NibblesPouchesUpsell: FC = () => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const [ canShowModal, setCanShowModal ] = useState<NibblesPouchesEligibilityState>("none");
  const [ subscriptionId, setSubscriptionId ] = useState<number | null>(null);
  const [ addNibblesResult, setAddNibblesResult ] = useState<NibblesPouchesPurchaseState>("none");
  const [ isModalOpen, setIsModalOpen ] = useState(true);

  const { accessToken } = useAuth();

  const subscriptionsAPI = useMemo(() => new SubscriptionsAPI(
    PublicConfig.NEXT_PUBLIC_SUBSCRIPTIONS_BASE_URL,
    accessToken,
  ), [ accessToken ]);

  const handleOnConfirm = useCallback(async () => {
    if (!subscriptionsAPI || !subscriptionId) {
      console.log("Subscriptions API or subscriptionId not found");
      return;
    }

    try {
      setAddNibblesResult("processing");
      const response = await subscriptionsAPI.updateExtras({
        subscriptionId,
        items: {
          [FreshExtraProduct.NIBBLES_CHICKEN_POUCH]: 1,
          [FreshExtraProduct.NIBBLES_SALMON_POUCH]: 1,
          [FreshExtraProduct.NIBBLES_TUNA_POUCH]: 1,
        },
      });
      if (response) {
        setAddNibblesResult("purchased");
      } else {
        setAddNibblesResult("failure");
      }
    } catch (error) {
      console.error("Error adding Nibbles pouches bundle", error);
      setAddNibblesResult("failure");
    }
  }, [ subscriptionsAPI, subscriptionId ]);

  const handleOnPickOwn = useCallback(() => {
    setIsModalOpen(false);
    router.push(MANAGE_EXTRAS_ROUTE);
  }, [ router ]);

  useEffect(() => {
    const determineIfModalShouldBeShown = async () => {
      if (!accessToken || !subscriptionsAPI) { return; }

      // Check if the server has already determined eligibility.
      const serverSelectedEligibility = searchParams.get(
        NIBBLES_POUCHES_UPSELL_OVERRIDE_KEY,
      ) as NibblesPouchesEligibilityState;

      if (serverSelectedEligibility === "eligible") {
        const serverSubscriptionId = searchParams.get(NIBBLES_POUCHES_UPSELL_SUBSCRIPTION_ID_KEY);
        if (serverSubscriptionId) {
          setSubscriptionId(Number(serverSubscriptionId));
          setCanShowModal("eligible");
        }
      } else if (serverSelectedEligibility === "none") {
        setCanShowModal("none");
      } else if (searchParams.has(NIBBLES_POUCHES_UPSELL_QUERY_KEY)) {
        // If the server has not determined eligibility, check locally.
        const eligibility = await checkNibblesPouchesUpsellEligibility(subscriptionsAPI);

        if (eligibility.eligible) {
          setSubscriptionId(eligibility.subscriptionId);
          setCanShowModal("eligible");
        } else {
          setCanShowModal("none");
        }
      }
    };

    void determineIfModalShouldBeShown();
  }, [ accessToken, subscriptionsAPI, searchParams ]);

  if (canShowModal !== "eligible" || !subscriptionId) {
    return null;
  }

  return (
    <NibblesPouchesUpsellModal
      id="nibbles-pouches-upsell"
      isOpen={ isModalOpen }
      theme="light-grey"
      modalTitleId="nibbles-pouches-upsell-title"
      modalDescriptionId="nibbles-pouches-upsell-description"
      onConfirm={ handleOnConfirm }
      onPickOwn={ handleOnPickOwn }
      onClose={ () => setIsModalOpen(false) }
      result={ addNibblesResult }
    />
  );
};

export default NibblesPouchesUpsell;
